import { TF } from 'react-translator-component';

import { IntegrationScope, IntegrationToken } from '../../../../types/responses/Interoperability';

/**
 * Rótulos e cores dos tokens de integração.
 *
 * Usa TF e não T onde o retorno é declarado string: o T devolve o array de nós do
 * react-html-parser, que mente no tipo e estoura na primeira operação de string.
 */

const SCOPE_LABELS: Record<IntegrationScope, string> = {
  READ: 'Read data',
  CHARGE_OPERATE: 'Operate charging',
  CONTRACT_MANAGE: 'Manage contracts',
};

const SCOPE_HELP: Record<IntegrationScope, string> = {
  READ: 'Query connections, cross charges, history and contract stations.',
  CHARGE_OPERATE:
    'Start and stop charging on the stations this contract reaches. This is the only scope with a physical effect.',
  CONTRACT_MANAGE:
    'Generate invites, accept, revoke and change scope. A compromised integrator can open your network to a third party.',
};

/**
 * Ordem em que os escopos aparecem: do menos ao mais perigoso.
 *
 * Não é enfeite. Quem marca de cima para baixo encontra o de efeito físico e o que abre a
 * rede por último, já tendo lido os dois primeiros.
 */
export const INTEGRATION_SCOPES: IntegrationScope[] = ['READ', 'CHARGE_OPERATE', 'CONTRACT_MANAGE'];

export const scopeLabel = (scope: IntegrationScope): string =>
  TF(SCOPE_LABELS[scope] || scope);

export const scopeHelp = (scope: IntegrationScope): string => TF(SCOPE_HELP[scope] || '');

/**
 * Escopos que merecem destaque de alerta na tela.
 *
 * Operar recarga chega ao carregador; gerenciar contrato abre a rede para terceiro. Ler
 * não tem como causar dano, então não polui.
 */
export const isSensitiveScope = (scope: IntegrationScope): boolean =>
  scope === 'CHARGE_OPERATE' || scope === 'CONTRACT_MANAGE';

const shortDateTime = (value: string | null): string => {
  if (!value) {
    return '';
  }

  const moment = new Date(value);

  if (Number.isNaN(moment.getTime())) {
    return '';
  }

  return moment.toLocaleString();
};

/** Estado do token, com a cor no mesmo código do resto do módulo. */
export const tokenStatus = (
  token: IntegrationToken,
): { label: string; dot: string; fg: string; bg: string } => {
  if (token.revoked_at) {
    return {
      label: TF('Revoked'),
      dot: '#c8352f',
      fg: '#b02525',
      bg: '#fdecec',
    };
  }

  if (!token.active) {
    // Não revogado e inativo só pode ser vencimento.
    return { label: TF('Expired'), dot: '#9aa3af', fg: '#6b7280', bg: '#eef0f2' };
  }

  return { label: TF('Active'), dot: '#26a35c', fg: '#1e7a45', bg: '#e7f6ed' };
};

/**
 * Texto do último uso.
 *
 * Nunca usado é informação, não ausência: token que nunca autenticou uma chamada é
 * integração que não entrou no ar, e dá para revogar sem medo.
 */
export const lastUseLabel = (token: IntegrationToken): string => {
  const when = shortDateTime(token.last_used_at);

  return when || TF('never used');
};

export const createdLabel = (token: IntegrationToken): string =>
  shortDateTime(token.created_at) || '—';
